'use client';

import React, { useState } from 'react';
import { useSession } from 'next-auth/react';
import { Button, DropdownMenu } from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';
import { LanguagesEnum } from 'abi-genome-common-types';

import { useToast } from '~/contexts';
import { Icon } from '~/shared/components/Icon';
import { useBlockDoubleTap, usePatchPreferredLanguage } from '~/shared/hooks';
import { cn } from '~/shared/utils/cn';
import { reloadLocation } from '~/shared/utils/fullPageNavigation';
import { getSafeTranslation } from '~/shared/utils/i18n';

type LanguageDropdownProps = {
  currentLanguage?: keyof typeof LanguagesEnum;
};

export const LanguageDropdownHeader = ({
  currentLanguage,
}: LanguageDropdownProps) => {
  const { update } = useSession();
  const { t } = useTranslate();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useBlockDoubleTap(() => setIsOpen(open => !open));

  const fallbackLanguage: keyof typeof LanguagesEnum = 'en-US';

  const safeLanguage =
    currentLanguage && currentLanguage in LanguagesEnum
      ? currentLanguage
      : fallbackLanguage;

  const userLanguage = safeLanguage.replaceAll(
    '_',
    '-'
  ) as keyof typeof LanguagesEnum;

  const currentLanguageLabel = getSafeTranslation(
    t,
    `languages.${userLanguage}`,
    getSafeTranslation(t, `languages.${fallbackLanguage}`, 'Language')
  );

  const updatePreferredLanguage = usePatchPreferredLanguage({
    async onSuccess() {
      toast.add({
        description: t('common.translations.preferred_language.success'),
        type: 'success',
      });

      // Reload so the server renders the new Tolgee locale and <html lang>.
      // During a proxy the PATCH changed the proxied user's preference, and
      // the session keeps the admin's locale.
      const updatedSession = await update();
      if (updatedSession) {
        reloadLocation();
        return;
      }

      toast.add({
        description: t('common.default.error'),
        type: 'error',
      });
    },
    onError() {
      toast.add({
        description: t('common.translations.preferred_language.error'),
        type: 'error',
      });
    },
  });

  const handleLanguageChange = (value: string) => {
    toast.add({
      description: t('common.translations.preferred_language.updating'),
      type: 'neutral',
    });
    updatePreferredLanguage.mutate({ preferredLanguage: value });
  };

  const languageOptions = Object.keys(LanguagesEnum).map(lang => ({
    label: getSafeTranslation(t, `languages.${lang}`, lang),
    onSelect: () => handleLanguageChange(lang),
  }));

  return (
    <DropdownMenu.Root modal open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        <Button
          size="icon"
          variant="white"
          border="none"
          title={currentLanguageLabel}
          className={cn(
            'text-[#191F2E] outline-none hover:bg-[#ededef]!',
            isOpen && 'bg-[#ededef]'
          )}
        >
          <Icon isCustom icon="translate" size={20} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        sideOffset={8}
        className="relative z-[1300] h-[448px] w-[calc(100vw-1px)] overflow-y-auto rounded-lg border-gray-410 shadow-[8px_0_16px_0_#191F2E3D] focus:outline-none md:w-fit"
      >
        {languageOptions.map(item => (
          <DropdownMenu.Item
            key={item.label}
            onSelect={item.onSelect}
            className="flex h-full flex-col items-start p-4 text-sm font-normal leading-4 text-[#191F2E]"
          >
            {item.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
